import React from 'react';
import {Typeahead} from '../components/typeahead';
import {useQuery} from '@apollo/react-hooks';
import gql from 'graphql-tag';
import {highlightResults} from './searchinput';
import {useDebounce} from './debounce';

export const toGqlVariables = (obj) => {
    const vars = [];
    const stringValues = ['startsWith']; // can add more keys in future if they have String as values
    Object.entries(obj).forEach(([key, val]) => {
        if (stringValues.includes(key)) {
            vars.push(`${key}: "${val}"`);
        } else {
            vars.push(`${key}: ${val}`);
        }
    });
    const varsBracket = vars.length ? `(${vars.join(', ')})` : '';
    return varsBracket;
};

export const usegraphQLResults = (
    search,
    shouldFetchHistory,
    shouldFetchNearbyResults,
    existingResults
) => {
    const filteredSearch =
        search && search.replace(/\\/g, '').replace(/\"/g, '');

    const queryArguments = {
        useHistory: shouldFetchHistory,
        startsWith: filteredSearch || ' ',
        drivable: shouldFetchNearbyResults,
        drivableEGTA: true,
        limit:
            shouldFetchNearbyResults && !shouldFetchHistory
                ? 8 - existingResults
                : 8
    };

    const GET_SUGGESTIONS = gql`
        query {
            typeAheadSuggestions${toGqlVariables(queryArguments)} {
                gaiaId
                term
    			type
                matchType
    			uuid
            }
        }
    `;
    const {data, loading} = useQuery(GET_SUGGESTIONS, {
        fetchPolicy: 'no-cache'
    });

    return {
        data,
        loading
    };
};

// eslint-disable-next-line complexity
export const selectRecentsAndNearbyfromEGTA = (suggestions) => {
    let results = [];
    for (let i = 0; i < suggestions.length; i++) {
        if (
            suggestions[i].matchType === 'RECENT_HISTORY' &&
            results.length < 2
        ) {
            results.push({
                recentlyviewed: true,
                ...suggestions[i]
            });
        } else if (
            suggestions[i].matchType === 'DRIVABLE' &&
            results.length < 8
        ) {
            results.push(suggestions[i]);
        }
    }
    return results;
};

export const countRecentAndNearby = (suggestions) => {
    let resultsCount = {
        recents: 0,
        nearBy: 0
    };
    if (suggestions) {
        for (let i = 0; i < suggestions.length; i++) {
            if (suggestions[i].matchType === 'RECENT_HISTORY') {
                resultsCount.recents += 1;
            } else if (suggestions[i].matchType === 'DRIVABLE') {
                resultsCount.nearBy += 1;
            }
        }
    }
    return resultsCount;
};

export const getRecentsAndNearbyResults = (graphQLResults) => {
    return graphQLResults.loading || !graphQLResults.data
        ? []
        : selectRecentsAndNearbyfromEGTA(
              graphQLResults.data.typeAheadSuggestions
          );
};

export const getResults = (
    isgraphQLResultsLoaded,
    resultsFromContainer,
    filteredResults
) => {
    return isgraphQLResultsLoaded ? resultsFromContainer : filteredResults;
};

export const filterSimilarResults = (
    resultWithoutHistory,
    historyResults,
    shouldFetchNearbyResults
) => {
    let recentResultsGaiaID = [];

    historyResults.forEach((value) => {
        recentResultsGaiaID.push(value.gaiaId);
    });

    let results = resultWithoutHistory.filter(
        (item) => !recentResultsGaiaID.includes(item.gaiaId)
    );

    // This check should be removed when we launch VRBO_LANDING_EGUAS_TYPEAHEAD experiment
    const finalfilteredResult = shouldFetchNearbyResults
        ? results.concat(historyResults)
        : historyResults.concat(results);
    return finalfilteredResult;
};

export const getFilteredResults = (
    resultsFromContainer,
    graphQLResults,
    shouldFetchNearbyResults
) => {
    return resultsFromContainer
        ? filterSimilarResults(
              resultsFromContainer,
              graphQLResults,
              shouldFetchNearbyResults
          )
        : graphQLResults;
};

export const getUnhighlightedResults = (graphQLResults, defaultResults) => {
    return graphQLResults.loading ? defaultResults : graphQLResults;
};

export const callOnResultsChange = (
    graphQLResults,
    onResultsChange,
    isTypeaheadPresented,
    resultsCount
) => {
    if (!graphQLResults.loading && isTypeaheadPresented) {
        onResultsChange(resultsCount.nearBy, resultsCount.recents);
    }
};

// eslint-disable-next-line complexity
const FunctionalContainer = (props) => {
    const {
        shouldFetchHistoryfromEGUAS: shouldFetchHistory,
        shouldFetchNearbyResults
    } = props;

    let newProps = {...props};

    if (shouldFetchHistory || shouldFetchNearbyResults) {
        const defaultResults = {loading: true, data: []};
        const debouncedSearch = useDebounce(props.search);
        const resultsFromContainer = props.results;

        const graphQLResults = usegraphQLResults(
            debouncedSearch,
            shouldFetchHistory,
            shouldFetchNearbyResults,
            resultsFromContainer ? resultsFromContainer.length : 0
        );
        const results = getUnhighlightedResults(graphQLResults, defaultResults);

        const highlightedgraphQLResults = highlightResults(
            props.search || ' ',
            getRecentsAndNearbyResults(results)
        );
        const filteredResults = getFilteredResults(
            resultsFromContainer,
            highlightedgraphQLResults,
            shouldFetchNearbyResults
        );

        newProps.results = getResults(
            graphQLResults.loading,
            resultsFromContainer,
            filteredResults
        );
        const resultsCount = countRecentAndNearby(newProps.results);
        callOnResultsChange(
            graphQLResults,
            props.onResultsChange,
            props.isTypeaheadPresented,
            resultsCount
        );
    }
    return <Typeahead {...newProps} />;
};

export default FunctionalContainer;
